import React, { useEffect, useState } from 'react';
import {
  FDBodyText,
  FDHeadlineText,
  FDTitleText,
} from '@facet-wealth/design';
import PropTypes from 'prop-types';
import {
  useScenarioDetailTableContext,
} from 'Views/Page/Content/Modal/Layouts/ScenarioSelectionAssumptionsAndDetails/Layout/ScenarioSelectionDetailedSummaryPicker/ScenarioSelectionDetailedSummary/ScenarioDetailTableContext';
import ScenarioSelectionDetailedSummarySectionRow from './ScenarioSelectionDetailedSummarySectionRow';

const componentOptionsByLevel = {
  0: { // Top level
    LabelComponent: FDHeadlineText,
    labelSize: 'sm',
    ValueComponent: FDTitleText,
    valueSize: 'lg',
  },
  1: { // Mid level
    LabelComponent: FDBodyText,
    labelSize: 'md',
    ValueComponent: FDTitleText,
    valueSize: 'md',
  },
  2: { // Low level
    LabelComponent: FDBodyText,
    labelSize: 'sm',
    ValueComponent: FDBodyText,
    valueSize: 'sm',
  },
};

const defaultComponentOptions = componentOptionsByLevel[2];
const getComponentOptionsByLevel = (level) => componentOptionsByLevel[level] || defaultComponentOptions;

// ScenarioSelectionDetailedSummarySectionAccordionRecursive is a recursive component that renders a nested list of data in an accordion format.
// This component is recursive because the data in the resource can have nested data, and this component is responsible for rendering that nested data.
// This also has a hard limit for levels of nesting to prevent infinite re-rendering.
const ScenarioSelectionDetailedSummarySectionAccordionRecursive = ({
  data, test, showMonths, level, dense,
}) => {
  const {
    rowFilter,
    expandAll,
  } = useScenarioDetailTableContext();

  const [toggledIDs, setToggledIDs] = useState(new Set());
  const isVisible = (id) => (
    expandAll
      // When expandAll is true, everything is expanded by default, so the toggled IDs are actually what we don't want to show
      ? !toggledIDs.has(id)
      // When expandAll is false, everything is collapsed by default, so the toggled IDs are what we want to show
      : toggledIDs.has(id)
  );
  // When toggling expandAll, toggledIDs will have the opposite meaning - let's clear it so that the member can quickly see what's going on
  useEffect(() => {
    setToggledIDs(new Set());
  }, [expandAll]);

  const handleToggleOpen = (expansionId) => {
    const updatedExpansionIds = new Set([...toggledIDs]);
    if (toggledIDs.has(expansionId)) {
      updatedExpansionIds.delete(expansionId);
    } else {
      updatedExpansionIds.add(expansionId);
    }
    setToggledIDs(updatedExpansionIds);
  };

  // If we select a filter that is not "All" we should auto-expand the top level accordion row
  useEffect(() => {
    if (rowFilter !== 'All' && data) {
      const selectedRow = data.find((r) => r.label === rowFilter);
      if (selectedRow) {
        handleToggleOpen(selectedRow.id);
      }
    }
  }, [rowFilter]);

  if (level > 6) {
    throw new Error('Developer error: Too many levels of nesting in ScenarioSelectionDetailedSummarySectionAccordionDetails, something went wrong');
  }

  if (!data || data.length === 0) {
    return null;
  }

  return (
    <>
      {data.map((row) => {
        const {
          LabelComponent, labelSize, ValueComponent, valueSize,
        } = getComponentOptionsByLevel(level);
        const expandable = row.rowSets && row.rowSets.length > 0;
        const valueColoredBySign = level === 0 && expandable;
        return (
          <React.Fragment key={row.id}>
            <ScenarioSelectionDetailedSummarySectionRow
              ValueComponent={ValueComponent}
              valueSize={valueSize}
              valueColoredBySign={valueColoredBySign}
              LabelComponent={LabelComponent}
              labelSize={labelSize}
              level={level}
              emphasized={row.hasEmphasis}
              row={row}
              expanded={isVisible(row.id)}
              handleToggleOpen={handleToggleOpen}
              isExpandable={expandable}
              showMonths={showMonths}
              test={test}
              dense={dense}
            />
            {isVisible(row.id) && (
              <ScenarioSelectionDetailedSummarySectionAccordionRecursive dense={dense} data={row.rowSets} test={`${test}-row-set`} level={level + 1} showMonths={showMonths} />
            )}
          </React.Fragment>
        );
      })}
    </>
  );
};

ScenarioSelectionDetailedSummarySectionAccordionRecursive.defaultProps = {
  test: 'detailed-scenario-table-row',
  level: 0,
};

ScenarioSelectionDetailedSummarySectionAccordionRecursive.propTypes = {
  data: PropTypes.array.isRequired,
  test: PropTypes.string,
  showMonths: PropTypes.bool.isRequired,
  level: PropTypes.number,
  dense: PropTypes.bool.isRequired,
};

export default ScenarioSelectionDetailedSummarySectionAccordionRecursive;
